<template>
    <div class="pt-tour">
        <slot
            :current-step="currentStep"
            :steps="steps"
            :previous-step="previousStep"
            :next-step="nextStep"
            :stop="stop"
            :skip="skip"
            :finish="finish"
            :is-first="isFirst"
            :is-last="isLast"
            :labels="customOptions.labels"
            :enabled-buttons="customOptions.enabledButtons"
            :highlight="customOptions.highlight"
            :debug="customOptions.debug"
        >
            <pt-tour-step
                v-if="steps[currentStep]"
                :step="steps[currentStep]"
                :key="currentStep"
                :previous-step="previousStep"
                :next-step="nextStep"
                :stop="stop"
                :skip="skip"
                :finish="finish"
                :is-first="isFirst"
                :is-last="isLast"
                :labels="customOptions.labels"
                :enabled-buttons="customOptions.enabledButtons"
                :highlight="customOptions.highlight"
                :stop-on-fail="customOptions.stopOnTargetNotFound"
                :debug="customOptions.debug"
                @targetNotFound="$emit('targetNotFound', $event)"
            >
            </pt-tour-step>
        </slot>
    </div>
</template>

<script lang="ts">
export default {
    name: 'pt-tour',
};
</script>

<script lang="ts" setup>
import { ref, computed, onMounted, onUnmounted, getCurrentInstance, expose } from 'vue';
import { DEFAULT_CALLBACKS, DEFAULT_OPTIONS, KEYS } from './constants';

// https://pulsardev.github.io/vue-tour/

interface Step {
    target: string;
    content: string;
}

interface Props {
    steps: Step[];
    name: string;
    options: DEFAULT_OPTIONS;
    callbacks: Object;
}

const props = withDefaults(defineProps<Props>(), {
    steps: [],
    options: DEFAULT_OPTIONS,
    callbacks: DEFAULT_CALLBACKS,
});

const instance = getCurrentInstance();
const globalProperties = instance?.appContext.config.globalProperties;
const currentStep = ref(-1);

const customOptions = computed(() => {
    return {
        ...DEFAULT_OPTIONS,
        ...props.options,
    };
});

const customCallbacks = computed(() => {
    return {
        ...DEFAULT_CALLBACKS,
        ...props.callbacks,
    };
});

const isRunning = computed(() => {
    return currentStep.value > -1 && currentStep.value < numberOfSteps.value;
});

const isFirst = computed(() => {
    return currentStep.value === 0;
});

const isLast = computed(() => {
    return currentStep.value === props.steps.length - 1;
});

const numberOfSteps = computed(() => {
    return props.steps.length;
});

const step = computed(() => {
    return props.steps[currentStep.value];
});

onMounted(() => {
    globalProperties.$tours[props.name] = {
        start,
    };
});

onUnmounted(() => {
    if (customOptions.value.useKeyboardNavigation) {
        window.removeEventListener('keyup', handleKeyup);
    }
});

async function start(startStep) {
    if (customOptions.value.useKeyboardNavigation) {
        window.addEventListener('keyup', handleKeyup);
    }

    startStep = typeof startStep !== 'undefined' ? parseInt(startStep, 10) : 0;
    let step = props.steps[startStep];

    let process = () =>
        new Promise((resolve, reject) => {
            setTimeout(() => {
                customCallbacks.value.onStart();
                currentStep.value = startStep;
                resolve();
            }, customOptions.value.startTimeout);
        });

    if (typeof step.before !== 'undefined') {
        try {
            await step.before('start');
        } catch (e) {
            return Promise.reject(e);
        }
    }
    await process();

    return Promise.resolve();
}

async function previousStep() {
    let futureStep = currentStep.value - 1;

    let process = () =>
        new Promise((resolve, reject) => {
            customCallbacks.value.onPreviousStep(currentStep.value);
            currentStep.value = futureStep;
            resolve();
        });

    if (futureStep > -1) {
        let step = props.steps[futureStep];
        if (typeof step.before !== 'undefined') {
            try {
                await step.before('previous');
            } catch (e) {
                return Promise.reject(e);
            }
        }
        await process();
    }

    return Promise.resolve();
}

async function nextStep() {
    let futureStep = currentStep.value + 1;

    let process = () =>
        new Promise((resolve, reject) => {
            customCallbacks.value.onNextStep(currentStep.value);
            currentStep.value = futureStep;
            resolve();
        });

    if (futureStep < numberOfSteps.value && currentStep.value !== -1) {
        let step = props.steps[futureStep];
        if (typeof step.before !== 'undefined') {
            try {
                await step.before('next');
            } catch (e) {
                return Promise.reject(e);
            }
        }
        await process();
    }

    return Promise.resolve();
}

function stop() {
    customCallbacks.value.onStop();
    document.body.classList.remove('pt-tour--active');
    currentStep.value = -1;
}

function skip() {
    customCallbacks.value.onSkip();
    stop();
}

function finish() {
    customCallbacks.value.onFinish();
    stop();
}

function handleKeyup(e) {
    if (customOptions.value.debug) {
        console.log('[Vue Tour] A keyup event occured:', e);
    }
    switch (e.keyCode) {
        case KEYS.ARROW_RIGHT:
            isKeyEnabled('arrowRight') && nextStep();
            break;
        case KEYS.ARROW_LEFT:
            isKeyEnabled('arrowLeft') && previousStep();
            break;
        case KEYS.ESCAPE:
            isKeyEnabled('escape') && stop();
            break;
    }
}

function isKeyEnabled(key) {
    const { enabledNavigationKeys } = customOptions.value;
    return enabledNavigationKeys.hasOwnProperty(key) ? enabledNavigationKeys[key] : true;
}
</script>

<style lang="scss">
body.pt-tour--active {
    pointer-events: none;
}

.pt-tour {
    pointer-events: auto;
}

.pt-tour__target--highlighted {
    box-shadow: 0 0 0 4px rgba(0, 0, 0, 0.4);
    pointer-events: auto;
    z-index: 9999;
}

.pt-tour__target--relative {
    position: relative;
}
</style>
